const BASE_URL = import.meta.env.VITE_BASE_URL;
import i18n from '@/locale';
import rules from '@/utils/rules';
import { DATE_FORMATS, BOOKING_STATUS } from '@/utils/config';
import dayjs from 'dayjs';

export default function (app) {
	app.mixin({
		computed: {
			emptyRules: () => [rules.emptyRules],
			emptyArrayRules: () => [rules.emptyArrayRules],
			minLengthRules: () => [rules.minLengthRules],
			maxLength25Rules: () => [rules.maxLength25Rules],
			maxLength255Rules: () => [rules.maxLength255Rules],
			emailRules: () => [rules.emailRules],
			passwordRules: () => [rules.passwordRules],
			urlRules: () => [rules.urlRules],
			expiredDateRules: () => [rules.expiredDateRules],
			cvvRules: () => [rules.cvvRules],
			cardNumberRules: () => [rules.cardNumberRules],
			phoneRules: () => [rules.phoneRules],
			emptyRuleWithParams: () => (isSubmitted, content) => [(v) => !isSubmitted || !!v || content],
			emptyArrayRuleWithParams: () => (isSubmitted, content) => rules.emptyArrayRuleWithParams(isSubmitted, content),
			emailRulesWithParams: () => (isSubmitted, content) => rules.emailRulesWithParams(isSubmitted, content),
			phoneRulesWithParams: () => (isSubmitted, content) => rules.phoneRulesWithParams(isSubmitted, content),
			passwordRulesWithParams: () => (isSubmitted, content) => rules.passwordRulesWithParams(isSubmitted, content),
			maxLength25RulesWithParams: () => (isSubmitted, content) => rules.maxLength25RulesWithParams(isSubmitted, content),
			confirmedValidator: () => (password) => [(v) => rules.confirmPasswordRules(v, password)],
		},
		methods: {
			/**
			 * i18n
			 */
			t(str, opt = {}) {
				if (!str) return str;
				return i18n.t(str, opt);
			},
			setLocale(lang) {
				i18n.locale = lang || 'en';
			},
			/**
			 * end i18n
			 */
			parseCurrency: (val) => {
				if (!val) return '0';

				return String(val || '').replace(/(\d)(?=(\d{3})+\b)/g, '$1.');
			},
			parsePhone: (val) => {
				return String(val || '').replace(/(\d{3})(\d{3})(\d{4})/g, '$1 $2 $3');
			},
			parseCCCD: (val) => {
				return String(val || '').replace(/(\d{3})(\d{3})(\d{3})(\d{3})/g, '$1 $2 $3 $4');
			},
			getNested(item, key) {
				try {
					return key.split('.').reduce((a, b) => a[b], item);
				} catch (err) {
					// FIXME TypeError: Cannot read properties of null (reading 'keyname')
					// console.log(err);
					return '';
				}
			},
			getName(list = [], obj) {
				const id = obj?.id || obj;
				const item = list.find((x) => x.id == id);
				return item?.name;
			},
			listDisc(items) {
				const arr = Array.isArray(items) ? items : [];
				let str = '';
				for (const el of arr) {
					str += `• ${this.truncateText(el.name, 42)}\n`;
				}
				return str;
			},
			expriedDate(end) {
				return expriedDate(end);
			},
			assetsUrl(relativePath) {
				return `${BASE_URL}${relativePath}`;
			},
			// apiUrl(relativePath) {
			// 	return `${HOST}${relativePath}`;
			// },
			saveFile(file, fileName) {
				const downloadLink = document.createElement('a');
				downloadLink.href = file;
				downloadLink.download = fileName;
				downloadLink.click();
			},
			truncateText(val, length = 40) {
				const more = val.length > length ? '...' : '';
				return val.substring(0, length) + more;
			},
			// parse errors from api
			parseErrors(code) {
				const err = code?.data?.errors || {};
				const data = {};
				for (const i in err) {
					const items = [];
					for (const el of err[i]) {
						items.push(i18n.t(el));
					}
					data[i] = items.join('\n');
				}
				return data;
			},
			formatDate(value) {
				if (!value) return '';
	
				return dayjs(String(value)).format(DATE_FORMATS.dd_mm_yyyy);
			},
			formatDateTime(value) {
				if (!value) return '';
	
				const dateTimePattern = `${DATE_FORMATS.dd_mm_yyyy} - HH:mm`
				return dayjs(String(value)).format(dateTimePattern);
			},

			// ==== Start booking status checkers ====
			isSearchingNurse(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_finding_nurse
				);
			},
			isWaitingNurseToAccept(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_waiting_nurse_to_accept
				);
			},
			isWaitingCustomerToAccept(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_waiting_customer_to_accept
				);
			},
			isNurseAssigned(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_nurse_assigned
				);
			},
			isWaitingPayment(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_waiting_payment
				);
			},
			isWaitingResult(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_waiting_result
				);
			},
			isPaid(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_paid
				);
			},
			isBookingCompleted(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_completed
				);
			},
			isBookingCancelled(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_cancelled ||
					bookingCard.status === BOOKING_STATUS.status_closed
				);
			},
			isBookingLabSample(bookingCard = {}) {
				if (bookingCard?.constructor !== Object) return false;

				return (
					bookingCard.status === BOOKING_STATUS.status_nurse_got_kit_test ||
					bookingCard.status === BOOKING_STATUS.status_nurse_got_sample_test ||
					bookingCard.status === BOOKING_STATUS.status_waiting_result
				);
			},
			// ==== End booking status checkers ====
		},
	})
}
